"use client"

import { useState, useEffect } from "react"
import { X, Save, RefreshCw, Edit, Calendar, DollarSign, FileText, AlertCircle, ArrowRightCircle } from "lucide-react"

const ContractActionModal = ({ isOpen, onClose, contract, mode, onSave, onRenew }) => {
  const [localMode, setLocalMode] = useState(mode)
  const [formData, setFormData] = useState({
    contractNumber: "",
    title: "",
    value: "",
    startDate: "",
    endDate: "",
    type: "",
    description: "",
    riskLevel: "",
    // Renewal specific fields
    newEndDate: "",
    newValue: "",
    renewalNotes: "",
    autoRenew: false,
  })

  // Synchronize localMode with the prop when modal opens or prop changes
  useEffect(() => {
    if (isOpen) {
      setLocalMode(mode)
    }
  }, [isOpen, mode])

  useEffect(() => {
    if (contract && isOpen) {
      if (localMode === "edit") {
        setFormData((prev) => ({
          ...prev,
          contractNumber: contract.contractNumber || "",
          title: contract.title || "",
          value: contract.value || "",
          startDate: contract.startDate ? contract.startDate.split("T")[0] : "",
          endDate: contract.endDate ? contract.endDate.split("T")[0] : "",
          type: contract.type || "",
          description: contract.description || "",
          riskLevel: contract.riskLevel || "medium",
        }))
      } else if (localMode === "renew") {
        const currentEndDate = new Date(contract.endDate)
        const suggestedNewEndDate = new Date(currentEndDate)
        suggestedNewEndDate.setFullYear(currentEndDate.getFullYear() + 1)

        const suggestedValue = Math.round(contract.value * 1.05) // 5% increase suggestion

        setFormData((prev) => ({
          ...prev,
          contractNumber: contract.contractNumber || "",
          title: contract.title || "",
          value: contract.value || "",
          startDate: contract.startDate ? contract.startDate.split("T")[0] : "",
          endDate: contract.endDate ? contract.endDate.split("T")[0] : "",
          type: contract.type || "",
          description: contract.description || "",
          riskLevel: contract.riskLevel || "medium",
          newEndDate: suggestedNewEndDate.toISOString().split("T")[0],
          newValue: suggestedValue,
          renewalNotes: `Renewal of ${contract.title} - Extended for 1 year with 5% value adjustment`,
          autoRenew: false,
        }))
      }
    }
  }, [contract, localMode, isOpen])

  const handleSubmit = (e) => {
    e.preventDefault()

    if (localMode === "edit") {
      const updatedContract = {
        ...contract,
        contractNumber: formData.contractNumber,
        title: formData.title,
        value: Number.parseFloat(formData.value),
        startDate: formData.startDate,
        endDate: formData.endDate,
        type: formData.type,
        description: formData.description,
        riskLevel: formData.riskLevel,
      }
      onSave(updatedContract)
    } else if (localMode === "renew") {
      const renewalData = {
        newEndDate: formData.newEndDate,
        newValue: Number.parseFloat(formData.newValue),
        renewalNotes: formData.renewalNotes,
        autoRenew: formData.autoRenew,
      }
      onRenew(renewalData)
    }

    onClose()
  }

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  if (!isOpen) return null

  const isEditMode = localMode === "edit"
  const isRenewMode = localMode === "renew"

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
      <div className="bg-white rounded-[2rem] shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto m-4 border border-gray-100">
        {/* Header */}
        <div className={`px-8 py-6 border-b border-gray-100 transition-colors duration-500 ${isEditMode ? "bg-orange-50/50" : "bg-emerald-50/50"}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className={`p-3 rounded-2xl shadow-sm ${isEditMode ? "bg-orange-100 text-orange-600" : "bg-emerald-100 text-emerald-600"}`}>
                {isEditMode ? <Edit size={24} /> : <RefreshCw size={24} />}
              </div>
              <div>
                <h2 className="text-xl font-black text-gray-800 uppercase tracking-tight">{isEditMode ? "Edit Contract" : "Renew Contract"}</h2>
                <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest mt-1 italic">{contract?.title}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {isEditMode && (
                <button
                  type="button"
                  onClick={() => setLocalMode("renew")}
                  className="flex items-center gap-2 bg-emerald-600 text-white px-4 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest hover:bg-emerald-700 transition-all shadow-lg shadow-emerald-100"
                >
                  <RefreshCw size={12} /> Switch to Renew
                </button>
              )}
              {isRenewMode && (
                <button
                  type="button"
                  onClick={() => setLocalMode("edit")}
                  className="flex items-center gap-2 bg-orange-600 text-white px-4 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest hover:bg-orange-700 transition-all shadow-lg shadow-orange-100"
                >
                  <Edit size={12} /> Switch to Edit
                </button>
              )}
              <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-400">
                <X size={20} />
              </button>
            </div>
          </div>
        </div>

        {/* Form Content */}
        <form onSubmit={handleSubmit} className="p-8 space-y-8">
          {/* Current Info Summary Card */}
          <div className="bg-gray-50/50 rounded-2xl p-6 border border-gray-100">
            <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4 flex items-center gap-2">
              <FileText size={14} /> Current Standard Registry Data
            </h3>
            <div className="grid grid-cols-2 gap-6 text-[11px] font-bold uppercase tracking-tight">
              <div>
                <span className="text-gray-400 block mb-1">Entity Name</span>
                <span className="text-gray-800">{contract?.vendor?.name}</span>
              </div>
              <div>
                <span className="text-gray-400 block mb-1">Contract Valuation</span>
                <span className="text-gray-800">${contract?.value?.toLocaleString()}</span>
              </div>
              <div>
                <span className="text-gray-400 block mb-1">Termination Date</span>
                <span className="text-gray-800">{contract?.endDate ? new Date(contract.endDate).toLocaleDateString() : "N/A"}</span>
              </div>
              <div>
                <span className="text-gray-400 block mb-1">Operational Status</span>
                <span className={`inline-block px-2 py-0.5 rounded text-[9px] font-black ${
                  contract?.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"
                }`}>
                  {contract?.status?.replace("_", " ")}
                </span>
              </div>
            </div>
          </div>

          {isEditMode ? (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Contract Title</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => handleInputChange("title", e.target.value)}
                    className="w-full p-4 bg-gray-50 border-none rounded-2xl text-xs font-bold focus:ring-2 focus:ring-orange-500 uppercase transition-all"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Classification</label>
                  <select
                    value={formData.type}
                    onChange={(e) => handleInputChange("type", e.target.value)}
                    className="w-full p-4 bg-gray-50 border-none rounded-2xl text-xs font-bold focus:ring-2 focus:ring-orange-500 uppercase transition-all"
                    required
                  >
                    <option value="service">Service</option>
                    <option value="product">Product</option>
                    <option value="supply">Supply</option>
                    <option value="licensing">Licensing</option>
                    <option value="maintenance">Maintenance</option>
                    <option value="consulting">Consulting</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Valuation ($)</label>
                  <input
                    type="number"
                    value={formData.value}
                    onChange={(e) => handleInputChange("value", e.target.value)}
                    className="w-full p-4 bg-gray-50 border-none rounded-2xl text-xs font-bold focus:ring-2 focus:ring-orange-500 transition-all"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Effective Date</label>
                  <input
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => handleInputChange("startDate", e.target.value)}
                    className="w-full p-4 bg-gray-50 border-none rounded-2xl text-xs font-bold focus:ring-2 focus:ring-orange-500 transition-all"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Termination Date</label>
                  <input
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => handleInputChange("endDate", e.target.value)}
                    className="w-full p-4 bg-gray-50 border-none rounded-2xl text-xs font-bold focus:ring-2 focus:ring-orange-500 transition-all"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                 <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Contract Number</label>
                  <input
                    type="text"
                    value={formData.contractNumber}
                    onChange={(e) => handleInputChange("contractNumber", e.target.value)}
                    className="w-full p-4 bg-gray-50 border-none rounded-2xl text-xs font-bold focus:ring-2 focus:ring-orange-500 uppercase transition-all"
                    required
                  />
                </div>
                 <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Risk Profile</label>
                  <select
                    value={formData.riskLevel}
                    onChange={(e) => handleInputChange("riskLevel", e.target.value)}
                    className="w-full p-4 bg-gray-50 border-none rounded-2xl text-xs font-bold focus:ring-2 focus:ring-orange-500 uppercase transition-all"
                  >
                    <option value="low">Low Risk</option>
                    <option value="medium">Medium Risk</option>
                    <option value="high">High Risk</option>
                  </select>
                </div>
              </div>
              <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Registry Memo</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleInputChange("description", e.target.value)}
                    rows="3"
                    className="w-full p-4 bg-gray-50 border-none rounded-2xl text-xs font-bold focus:ring-2 focus:ring-orange-500 uppercase transition-all resize-none"
                    placeholder="KEY TERMS OR OBLIGATIONS..."
                  />
              </div>
            </div>
          ) : (
            <div className="space-y-8">
              <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-2">
                  <AlertCircle className="text-emerald-600" size={20} />
                  <h3 className="text-[10px] font-black text-emerald-800 uppercase tracking-widest">Renewal Protocol Active</h3>
                </div>
                <p className="text-[10px] font-bold text-emerald-700 uppercase leading-relaxed tracking-tight">
                  Configuring extension terms. System suggests a 12-month extension with a 5% standard valuation adjustment.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Extended Termination Date</label>
                  <input
                    type="date"
                    value={formData.newEndDate}
                    onChange={(e) => handleInputChange("newEndDate", e.target.value)}
                    className="w-full p-4 bg-emerald-50/30 border-none rounded-2xl text-xs font-bold focus:ring-2 focus:ring-emerald-500 transition-all"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Adjusted Valuation ($)</label>
                  <input
                    type="number"
                    value={formData.newValue}
                    onChange={(e) => handleInputChange("newValue", e.target.value)}
                    className="w-full p-4 bg-emerald-50/30 border-none rounded-2xl text-xs font-bold focus:ring-2 focus:ring-emerald-500 transition-all"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Renewal Protocol Notes</label>
                <textarea
                  value={formData.renewalNotes}
                  onChange={(e) => handleInputChange("renewalNotes", e.target.value)}
                  rows="3"
                  className="w-full p-4 bg-emerald-50/30 border-none rounded-2xl text-xs font-bold focus:ring-2 focus:ring-emerald-500 uppercase transition-all resize-none"
                  placeholder="ADD RENEWAL NOTES OR CHANGE LOG..."
                />
              </div>

              <label className="flex items-center gap-3 group cursor-pointer w-fit">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={formData.autoRenew}
                    onChange={(e) => handleInputChange("autoRenew", e.target.checked)}
                    className="peer sr-only"
                  />
                  <div className="w-10 h-6 bg-gray-200 rounded-full peer peer-checked:bg-emerald-500 transition-all duration-300 shadow-inner"></div>
                  <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-all duration-300 shadow-md peer-checked:translate-x-4"></div>
                </div>
                <span className="text-[10px] font-black text-gray-500 uppercase tracking-widest group-hover:text-emerald-600 transition-colors">
                  Enable Cycle-Based Auto-Renewal
                </span>
              </label>
            </div>
          )}

          {/* Action Footer */}
          <div className="flex justify-end gap-3 pt-8 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="px-8 py-3 bg-gray-100 text-gray-500 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-gray-200 transition-all"
            >
              Cancel Operation
            </button>
            <button
              type="submit"
              className={`flex items-center gap-2 px-10 py-3 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all shadow-lg ${
                isEditMode 
                  ? "bg-gray-900 hover:bg-orange-600 shadow-gray-100 hover:shadow-orange-100" 
                  : "bg-emerald-600 hover:bg-emerald-700 shadow-emerald-100"
              }`}
            >
              {isEditMode ? <Save size={14} /> : <RefreshCw size={14} />}
              {isEditMode ? "Commit Registry Changes" : "Apply Extension Protocol"}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export { ContractActionModal }
export default ContractActionModal
